/**
 * Mock preview data for each widget type, used by WidgetPreviewRenderer
 * to render widget thumbnails in the library grid and detail view.
 *
 * - activity / notes: shape expected by their dedicated components
 * - all others: generic Widget shape { title, items: [{ id, content }] }
 */

const WIDGET_PREVIEW_DATA = Object.freeze({
  activity: {
    items: [
      {
        id: 'p1',
        type: 'call',
        title: 'Title of the activity',
        description: 'Customer requested a follow-up call.',
        author: 'Robert Chen',
        timeAgo: '4h ago',
        alerts: [{ type: 'needs_attention', message: 'Follow-up required' }],
        metadata: {
          userId: 'usr-001',
          sentiment: 'positive',
          assignedTasks: 2,
          hasRecording: true,
          hasExternalLink: true,
          direction: 'outbound',
          duration: '4 mins',
          flagged: true,
        },
      },
      {
        id: 'p2',
        type: 'email',
        title: 'Title of the activity',
        description: 'Additional warranty information requested.',
        author: 'Maria Garcia',
        timeAgo: '4h ago',
        metadata: {
          userId: 'usr-002',
          commentsCount: 7,
          attachmentsCount: 3,
          assignedTasks: 2,
          hasExternalLink: true,
          flagged: true,
        },
      },
      {
        id: 'p3',
        type: 'call',
        title: 'Title of the activity',
        description: 'Technical support regarding the mobile app.',
        timeAgo: '1d ago',
        alerts: [{ type: 'expired', message: 'Task expired' }],
        metadata: {
          userId: 'usr-003',
          duration: '12 mins',
          hasExternalLink: true,
          flagged: true,
        },
      },
      {
        id: 'p4',
        type: 'sms',
        title: 'Title of the activity',
        description: 'Appointment confirmation sent.',
        timeAgo: '2d ago',
        metadata: {
          userId: 'usr-004',
          commentsCount: 7,
          attachmentsCount: 3,
          assignedTasks: 2,
          hasExternalLink: true,
          linkCount: 1,
          flagged: true,
        },
      },
    ],
  },

  notes: {
    items: [
      {
        id: 'p1',
        title: 'Customer appointment reminder',
        description: 'Follow-up call to discuss financing options.',
        dueDate: 'Mar 4',
        author: 'David Kim',
        timeAgo: '4h ago',
        metadata: { commentsCount: 7, attachmentsCount: 3, tasksCount: 2 },
      },
      {
        id: 'p2',
        title: 'Financing options discussion',
        description: 'Reviewed 60-month vs 72-month terms.',
        dueDate: 'Mar 2',
        author: 'Maria Garcia',
        timeAgo: '1d ago',
        metadata: { commentsCount: 2, attachmentsCount: 1, tasksCount: 0 },
      },
      {
        id: 'p3',
        title: 'Test drive scheduled',
        description: 'Confirmed for Saturday 10am.',
        dueDate: 'Feb 24',
        author: 'Robert Chen',
        timeAgo: '2d ago',
        metadata: { commentsCount: 0, attachmentsCount: 0, tasksCount: 1 },
      },
    ],
  },

  emails: {
    title: 'Emails',
    items: [
      { id: 'p1', content: 'Re: Warranty renewal confirmation' },
      { id: 'p2', content: 'Service appointment follow-up' },
      { id: 'p3', content: 'New financing offer available' },
      { id: 'p4', content: 'Trade-in estimate request' },
    ],
  },

  chat: {
    title: 'Chat Messages',
    items: [
      { id: 'p1', content: 'Customer inquiry about delivery ETA' },
      { id: 'p2', content: 'Internal: Parts availability update' },
      { id: 'p3', content: 'Follow-up on service complaint' },
      { id: 'p4', content: 'New lead from web form' },
    ],
  },

  appointments: {
    title: 'Appointments',
    items: [
      { id: 'p1', content: 'Test drive — Toyota Camry' },
      { id: 'p2', content: 'Finance consultation' },
      { id: 'p3', content: 'Vehicle delivery walkthrough' },
    ],
  },

  tasks: {
    title: 'Tasks',
    items: [
      { id: 'p1', content: 'Prepare trade-in appraisal' },
      { id: 'p2', content: 'Send financing documents' },
      { id: 'p3', content: 'Schedule vehicle inspection' },
      { id: 'p4', content: 'Update CRM contact record' },
    ],
  },

  followUps: {
    title: 'Follow-ups',
    items: [
      { id: 'p1', content: 'Call back about lease options' },
      { id: 'p2', content: 'Send updated quote' },
      { id: 'p3', content: 'Check loan approval status' },
    ],
  },

  pipeline: {
    title: 'Pipeline Overview',
    items: [
      { id: 'p1', content: 'New Leads — 24 deals ($180k)' },
      { id: 'p2', content: 'Qualified — 18 deals ($290k)' },
      { id: 'p3', content: 'Proposal Sent — 12 deals ($410k)' },
      { id: 'p4', content: 'Negotiation — 6 deals ($220k)' },
      { id: 'p5', content: 'Closed Won — 3 deals ($150k)' },
    ],
  },

  conversionFunnel: {
    title: 'Conversion Funnel',
    items: [
      { id: 'p1', content: 'Visitors → Leads: 12.4%' },
      { id: 'p2', content: 'Leads → Qualified: 45.2%' },
      { id: 'p3', content: 'Qualified → Proposal: 68.1%' },
      { id: 'p4', content: 'Proposal → Closed: 32.7%' },
    ],
  },

  performanceScore: {
    title: 'Performance Score',
    items: [
      { id: 'p1', content: 'Overall Score: 87/100' },
      { id: 'p2', content: 'Response Time: 94/100' },
      { id: 'p3', content: 'Customer Satisfaction: 82/100' },
    ],
  },

  aiInsights: {
    title: 'AI Insights',
    items: [
      { id: 'p1', content: 'High churn risk detected for 3 accounts' },
      { id: 'p2', content: 'Upsell opportunity: Premium service plan' },
      { id: 'p3', content: 'Best time to contact: Tue 10am–12pm' },
      { id: 'p4', content: 'Sentiment trending positive this week' },
    ],
  },

  sentiment: {
    title: 'Sentiment Analysis',
    items: [
      { id: 'p1', content: 'Positive: 64% of interactions' },
      { id: 'p2', content: 'Neutral: 28% of interactions' },
      { id: 'p3', content: 'Negative: 8% of interactions' },
      { id: 'p4', content: 'Trend: +5% positive vs last month' },
    ],
  },
});

export default WIDGET_PREVIEW_DATA;
